<script>
/**
 * Painel de respostas de uma campanha — /app/campanha/respostas?id=N
 *
 * Responde "o que essa campanha me trouxe de volta": quantos responderam, em
 * quanto tempo, o que disseram (com botões de template somados por opção) e
 * quem ficou em silêncio ou nem recebeu.
 *
 * A atribuição é feita no backend (CampanhaRespostaService): resposta citando o
 * disparo entra sempre; sem citação, entra o que chegou dentro da janela
 * configurada em Customização → Parâmetros. Campanhas antigas não têm nada
 * gravado — o botão "Recalcular" varre o histórico e preenche.
 *
 * Gráficos são barras em CSS, com tokens do tema: acompanham light e dark sem
 * reinicializar nada (echarts exigiria observar a troca de tema).
 */
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import { api, sucesso, erro } from '@/helpers/common/api';
import CampanhaRelatorioModal from '@/components/crm/CampanhaRelatorioModal.vue';
import socketClient from '@/services/socketClient';

const POR_PAGINA = 25;

export default {
  name: 'CampanhaRespostas',
  components: { Layout, PageHeader, CampanhaRelatorioModal },
  data() {
    return {
      resumo: null,
      carregando: true,
      recalculando: false,
      modalRelatorio: false,

      aba: 'respondidos',
      linhas: [],
      total: 0,
      pagina: 1,
      busca: '',
      carregandoLista: false,
      buscaTimer: null,

      expandido: null,        // contato_id com a conversa aberta na linha
      mensagens: [],          // mensagens do contato expandido
      carregandoMensagens: false,

      verTodosGrupos: false,
      socketOff: null,
    };
  },
  computed: {
    campanhaId() { return Number(this.$route.query.id || this.$route.query.campanha || 0); },
    campanha() { return (this.resumo && this.resumo.campanha) || {}; },
    kpis() { return (this.resumo && this.resumo.kpis) || {}; },
    agrupamento() {
      const lista = (this.resumo && this.resumo.agrupamento) || [];
      return this.verTodosGrupos ? lista : lista.slice(0, 10);
    },
    temMaisGrupos() {
      return ((this.resumo && this.resumo.agrupamento) || []).length > 10;
    },
    funil() {
      const k = this.kpis;
      const base = k.enviados || 0;
      const pct = (v) => (base ? Math.round((v / base) * 1000) / 10 : 0);
      return [
        { rotulo: 'Enviados', total: k.enviados || 0, percentual: pct(k.enviados || 0), cor: 'secondary' },
        { rotulo: 'Entregues', total: k.entregues || 0, percentual: pct(k.entregues || 0), cor: 'info' },
        { rotulo: 'Lidos', total: k.lidos || 0, percentual: pct(k.lidos || 0), cor: 'primary' },
        { rotulo: 'Responderam', total: k.respondentes || 0, percentual: pct(k.respondentes || 0), cor: 'success' },
      ];
    },
    tempoResposta() { return (this.resumo && this.resumo.tempoResposta) || []; },
    totalPaginas() { return Math.max(Math.ceil(this.total / POR_PAGINA), 1); },
    abas() {
      const k = this.kpis;
      return [
        { chave: 'respondidos', rotulo: 'Responderam', contagem: k.respondentes || 0, icone: 'ri-chat-smile-2-line' },
        { chave: 'sem_resposta', rotulo: 'Sem resposta', contagem: k.semResposta || 0, icone: 'ri-user-search-line' },
        { chave: 'falhas', rotulo: 'Não recebeu', contagem: (k.falhas || 0) + (k.bloqueados || 0), icone: 'ri-error-warning-line' },
      ];
    },
  },
  async created() {
    if (!this.campanhaId) {
      erro({ title: 'Campanha não informada.' });
      this.carregando = false;
      return;
    }
    await this.carregarResumo();
    await this.carregarLista();
    this._registrarSocket();
  },
  beforeUnmount() {
    if (this.socketOff) { try { this.socketOff(); } catch (_e) { /* noop */ } }
    if (this.buscaTimer) clearTimeout(this.buscaTimer);
  },
  methods: {
    _registrarSocket() {
      try {
        // Resposta nova chegando enquanto a tela está aberta: atualiza sem F5.
        this.socketOff = socketClient.on('campanha:resposta', ({ campanha_id: id }) => {
          if (Number(id) !== this.campanhaId) return;
          this.carregarResumo({ silencioso: true });
          this.carregarLista({ silencioso: true });
        });
      } catch (_e) { /* sem socket disponível */ }
    },

    async carregarResumo({ silencioso = false } = {}) {
      if (!silencioso) this.carregando = true;
      try {
        const r = await api.read(`/campanha/respostas/${this.campanhaId}`);
        if (r && r.kpis) this.resumo = r;
        else if (!silencioso) erro({ title: 'Não foi possível carregar o resumo da campanha.' });
      } finally {
        this.carregando = false;
      }
    },

    async carregarLista({ silencioso = false } = {}) {
      if (!silencioso) this.carregandoLista = true;
      try {
        const params = new URLSearchParams({
          aba: this.aba,
          limit: String(POR_PAGINA),
          offset: String((this.pagina - 1) * POR_PAGINA),
        });
        if (this.busca.trim()) params.set('busca', this.busca.trim());
        const r = await api.read(`/campanha/respostasLista/${this.campanhaId}?${params.toString()}`);
        this.linhas = (r && r.data) || [];
        this.total = (r && r.total) || 0;
      } finally {
        this.carregandoLista = false;
      }
    },

    trocarAba(chave) {
      if (this.aba === chave) return;
      this.aba = chave;
      this.pagina = 1;
      this.expandido = null;
      this.carregarLista();
    },

    onBusca() {
      if (this.buscaTimer) clearTimeout(this.buscaTimer);
      this.buscaTimer = setTimeout(() => {
        this.pagina = 1;
        this.carregarLista();
      }, 350);
    },

    irPara(pagina) {
      if (pagina < 1 || pagina > this.totalPaginas) return;
      this.pagina = pagina;
      this.expandido = null;
      this.carregarLista();
    },

    /** Abre/fecha a conversa completa daquela pessoa dentro da própria linha. */
    async alternarConversa(linha) {
      if (this.expandido === linha.contato_id) {
        this.expandido = null;
        this.mensagens = [];
        return;
      }
      this.expandido = linha.contato_id;
      this.mensagens = [];
      if ((linha.total_mensagens || 1) <= 1) {
        this.mensagens = [{
          id: linha.id,
          ordem: 1,
          tipo: linha.tipo,
          opcao: linha.opcao,
          conteudo: linha.conteudo,
          recebida_em: linha.recebida_em,
          minutos_ate_resposta: linha.minutos_ate_resposta,
        }];
        return;
      }
      this.carregandoMensagens = true;
      try {
        const r = await api.read(`/campanha/respostasContato/${this.campanhaId}?contatoId=${linha.contato_id}`);
        this.mensagens = Array.isArray(r) ? r : [];
      } finally {
        this.carregandoMensagens = false;
      }
    },

    abrirConversa(linha) {
      if (!linha.conversa_id) return;
      this.$router.push({ path: '/app/atendimento', query: { conversaId: linha.conversa_id } });
    },

    async recalcular() {
      if (this.recalculando) return;
      this.recalculando = true;
      try {
        const r = await api.create(`/campanha/recalcularRespostas/${this.campanhaId}`, {});
        if (r && r.message) sucesso({ title: r.message });
        else erro({ title: 'Não foi possível recalcular as respostas.' });
        await this.carregarResumo({ silencioso: true });
        await this.carregarLista({ silencioso: true });
      } finally {
        this.recalculando = false;
      }
    },

    voltar() {
      this.$router.push({ path: '/app/campanha' });
    },

    // ---- formatação ----
    dataHora(valor) {
      if (!valor) return '—';
      const d = new Date(valor);
      if (Number.isNaN(d.getTime())) return '—';
      return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
    },
    duracao(minutos) {
      if (minutos === null || minutos === undefined) return '—';
      const m = Number(minutos);
      if (!Number.isFinite(m)) return '—';
      if (m < 60) return `${m} min`;
      const horas = Math.floor(m / 60);
      const resto = m % 60;
      if (horas < 24) return resto ? `${horas}h ${resto}min` : `${horas}h`;
      const dias = Math.floor(horas / 24);
      const hResto = horas % 24;
      return hResto ? `${dias}d ${hResto}h` : `${dias}d`;
    },
    telefone(valor) {
      const d = String(valor || '').replace(/\D/g, '');
      if (!d) return '—';
      const br = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
      if (br.length === 11) return `(${br.slice(0, 2)}) ${br.slice(2, 7)}-${br.slice(7)}`;
      if (br.length === 10) return `(${br.slice(0, 2)}) ${br.slice(2, 6)}-${br.slice(6)}`;
      return d;
    },
    local(linha) {
      return [linha.cidade, linha.estado].filter(Boolean).join('/') || '—';
    },
    textoResposta(linha) {
      if (linha.opcao) return linha.opcao;
      if (linha.tipo === 'midia') return 'Enviou uma mídia';
      if (linha.tipo === 'localizacao') return `Localização ${linha.conteudo || ''}`.trim();
      return linha.conteudo || '—';
    },
    statusLabel(status) {
      const map = {
        enviado: 'Enviado', entregue: 'Entregue', lido: 'Lido', respondido: 'Respondido',
        falha: 'Falha no envio', sem_consentimento: 'Sem opt-in', sem_telefone: 'Sem telefone',
        optout: 'Optou por sair', cancelado: 'Cancelado',
      };
      return map[status] || status;
    },
    statusBadge(status) {
      const map = {
        enviado: 'badge bg-secondary-subtle text-secondary',
        entregue: 'badge bg-info-subtle text-info',
        lido: 'badge bg-primary-subtle text-primary',
        respondido: 'badge bg-success-subtle text-success',
        falha: 'badge bg-danger-subtle text-danger',
        sem_consentimento: 'badge bg-warning-subtle text-warning',
        sem_telefone: 'badge bg-warning-subtle text-warning',
        optout: 'badge bg-dark-subtle text-body',
        cancelado: 'badge bg-dark-subtle text-body',
      };
      return map[status] || 'badge bg-secondary-subtle text-secondary';
    },
    campanhaStatusBadge(status) {
      const map = {
        em_execucao: 'badge bg-success-subtle text-success',
        pausada: 'badge bg-warning-subtle text-warning',
        concluida: 'badge bg-primary-subtle text-primary',
        cancelada: 'badge bg-danger-subtle text-danger',
      };
      return map[status] || 'badge bg-secondary-subtle text-secondary';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Respostas da campanha" pageTitle="Campanhas" />

    <div v-if="carregando" class="text-center py-5"><BSpinner variant="primary" /></div>

    <template v-else-if="resumo">
      <!-- ===== Cabeçalho da campanha + ações ===== -->
      <BCard no-body class="mb-3">
        <div class="card-body d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <h5 class="m-0">{{ campanha.nome }}</h5>
              <span :class="campanhaStatusBadge(campanha.status)">{{ campanha.status }}</span>
            </div>
            <div class="text-muted small mt-1">
              <span v-if="campanha.numero"><i class="ri-whatsapp-line align-bottom me-1"></i>{{ campanha.numero }} · </span>
              <span v-if="campanha.template_nome">Template <code>{{ campanha.template_nome }}</code> · </span>
              <span>Disparo iniciado em {{ dataHora(campanha.iniciada_em) }}</span>
            </div>
            <div class="text-muted mt-1" style="font-size: 11.5px;">
              <i class="ri-time-line align-bottom me-1"></i>
              Conta como resposta o que chega até {{ resumo.janelaHoras }}h depois do envio
              (ou qualquer mensagem que cite o disparo).
            </div>
          </div>

          <div class="hstack gap-2 flex-wrap">
            <BButton variant="light" size="sm" @click="voltar">
              <i class="ri-arrow-left-line align-bottom me-1"></i> Campanhas
            </BButton>
            <BButton variant="soft-primary" size="sm" :disabled="recalculando" @click="recalcular"
              title="Varre o histórico de mensagens e atribui o que faltava — use em campanhas antigas.">
              <BSpinner v-if="recalculando" small class="me-1" />
              <i v-else class="ri-refresh-line align-bottom me-1"></i>
              Recalcular respostas
            </BButton>
            <BButton variant="primary" size="sm" @click="modalRelatorio = true">
              <i class="ri-file-chart-line align-bottom me-1"></i> Exportar relatório
            </BButton>
          </div>
        </div>
      </BCard>

      <!-- ===== KPIs ===== -->
      <BRow class="g-3 mb-1">
        <BCol xl="3" md="6">
          <BCard no-body class="h-100">
            <div class="card-body">
              <p class="text-uppercase text-muted fw-medium mb-2" style="font-size: 11px;">Responderam</p>
              <h3 class="mb-0 text-success">{{ kpis.respondentes || 0 }}</h3>
              <p class="text-muted small mb-0 mt-1">
                {{ kpis.taxaResposta || 0 }}% de quem recebeu ·
                {{ kpis.respostas || 0 }} mensagem(ns)
              </p>
            </div>
          </BCard>
        </BCol>
        <BCol xl="3" md="6">
          <BCard no-body class="h-100">
            <div class="card-body">
              <p class="text-uppercase text-muted fw-medium mb-2" style="font-size: 11px;">Entregues</p>
              <h3 class="mb-0">{{ kpis.entregues || 0 }}</h3>
              <p class="text-muted small mb-0 mt-1">
                {{ kpis.taxaEntrega || 0 }}% dos enviados · {{ kpis.lidos || 0 }} lido(s)
              </p>
            </div>
          </BCard>
        </BCol>
        <BCol xl="3" md="6">
          <BCard no-body class="h-100">
            <div class="card-body">
              <p class="text-uppercase text-muted fw-medium mb-2" style="font-size: 11px;">Tempo até responder</p>
              <h3 class="mb-0">{{ duracao(kpis.tempoMedianoMinutos) }}</h3>
              <p class="text-muted small mb-0 mt-1">mediana · média {{ duracao(kpis.tempoMedioMinutos) }}</p>
            </div>
          </BCard>
        </BCol>
        <BCol xl="3" md="6">
          <BCard no-body class="h-100">
            <div class="card-body">
              <p class="text-uppercase text-muted fw-medium mb-2" style="font-size: 11px;">Sem retorno</p>
              <h3 class="mb-0 text-warning">{{ kpis.semResposta || 0 }}</h3>
              <p class="text-muted small mb-0 mt-1">
                saíram e não responderam · {{ kpis.falhas || 0 }} falha(s)
              </p>
            </div>
          </BCard>
        </BCol>
      </BRow>

      <!-- ===== Funil + tempo de resposta ===== -->
      <BRow class="g-3 mb-1">
        <BCol lg="6">
          <BCard no-body class="h-100">
            <div class="card-body">
              <h6 class="mb-3">Funil do disparo</h6>
              <div v-for="f in funil" :key="f.rotulo" class="mb-3">
                <div class="d-flex justify-content-between small mb-1">
                  <span>{{ f.rotulo }}</span>
                  <span class="text-muted">{{ f.total }} · {{ f.percentual }}%</span>
                </div>
                <div class="progress" style="height: 8px;">
                  <div class="progress-bar" :class="`bg-${f.cor}`" :style="`width: ${f.percentual}%`"></div>
                </div>
              </div>
            </div>
          </BCard>
        </BCol>
        <BCol lg="6">
          <BCard no-body class="h-100">
            <div class="card-body">
              <h6 class="mb-3">Em quanto tempo responderam</h6>
              <div v-if="!kpis.respondentes" class="text-muted small">Ainda não há respostas para medir.</div>
              <div v-for="f in tempoResposta" v-else :key="f.chave" class="mb-3">
                <div class="d-flex justify-content-between small mb-1">
                  <span>{{ f.rotulo }}</span>
                  <span class="text-muted">{{ f.total }} · {{ f.percentual }}%</span>
                </div>
                <div class="progress" style="height: 8px;">
                  <div class="progress-bar bg-info" :style="`width: ${f.percentual}%`"></div>
                </div>
              </div>
            </div>
          </BCard>
        </BCol>
      </BRow>

      <!-- ===== O que responderam ===== -->
      <BCard no-body class="mb-1">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
            <div>
              <h6 class="mb-1">O que responderam</h6>
              <p class="text-muted small mb-0">
                Respostas iguais somadas — botões do template viram uma linha por opção;
                textos curtos são agrupados ignorando acento e maiúscula.
              </p>
            </div>
            <BButton v-if="temMaisGrupos" variant="link" size="sm" class="p-0"
              @click="verTodosGrupos = !verTodosGrupos">
              {{ verTodosGrupos ? 'Ver menos' : 'Ver todos' }}
            </BButton>
          </div>

          <div v-if="!agrupamento.length" class="text-muted small">
            Nenhuma resposta atribuída a esta campanha até agora.
            <template v-if="campanha.status === 'concluida'">
              Se a campanha é antiga, use <strong>Recalcular respostas</strong> para varrer o histórico.
            </template>
          </div>

          <div v-for="g in agrupamento" :key="g.chave" class="mb-3">
            <div class="d-flex justify-content-between align-items-center small mb-1 gap-2">
              <span class="text-truncate">
                <i v-if="g.tipo === 'botao'" class="ri-cursor-line text-success me-1"></i>
                {{ g.rotulo }}
              </span>
              <span class="text-muted text-nowrap">{{ g.total }} · {{ g.percentual }}%</span>
            </div>
            <div class="progress" style="height: 8px;">
              <div class="progress-bar" :class="g.tipo === 'botao' ? 'bg-success' : 'bg-secondary'"
                :style="`width: ${g.percentual}%`"></div>
            </div>
          </div>
        </div>
      </BCard>

      <!-- ===== Listas ===== -->
      <BCard no-body>
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div class="btn-group btn-group-sm" role="group">
              <button v-for="a in abas" :key="a.chave" type="button" class="btn"
                :class="aba === a.chave ? 'btn-primary' : 'btn-outline-primary'"
                @click="trocarAba(a.chave)">
                <i :class="a.icone" class="align-bottom me-1"></i>
                {{ a.rotulo }} <span class="badge bg-body-secondary text-body-secondary ms-1">{{ a.contagem }}</span>
              </button>
            </div>
            <div class="search-box">
              <input v-model="busca" type="text" class="form-control form-control-sm"
                placeholder="Buscar por nome…" @input="onBusca" />
            </div>
          </div>

          <div v-if="carregandoLista" class="text-center py-4"><BSpinner variant="primary" /></div>

          <div v-else class="table-responsive">
            <!-- Aba: responderam -->
            <table v-if="aba === 'respondidos'" class="table align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th>Contato</th>
                  <th>Telefone</th>
                  <th>Respondeu em</th>
                  <th>Depois de</th>
                  <th>Resposta</th>
                  <th class="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="l in linhas" :key="l.id">
                  <tr>
                    <td>
                      <div class="fw-medium">{{ l.nome || '—' }}</div>
                      <small class="text-muted">{{ local(l) }}</small>
                    </td>
                    <td class="text-nowrap"><small>{{ telefone(l.telefone) }}</small></td>
                    <td class="text-nowrap"><small>{{ dataHora(l.recebida_em) }}</small></td>
                    <td class="text-nowrap"><small>{{ duracao(l.minutos_ate_resposta) }}</small></td>
                    <td style="min-width: 240px; max-width: 380px;">
                      <span v-if="l.opcao" class="badge bg-success-subtle text-success me-1">
                        <i class="ri-cursor-line me-1"></i>botão
                      </span>
                      <span class="resposta-texto">{{ textoResposta(l) }}</span>
                      <div v-if="l.total_mensagens > 1" class="mt-1">
                        <button type="button" class="btn btn-link btn-sm p-0"
                          @click="alternarConversa(l)">
                          {{ expandido === l.contato_id ? 'Ocultar' : `+ ${l.total_mensagens - 1} mensagem(ns)` }}
                        </button>
                      </div>
                    </td>
                    <td class="text-end">
                      <BButton v-if="l.conversa_id" variant="soft-primary" size="sm"
                        title="Abrir a conversa no atendimento" @click="abrirConversa(l)">
                        <i class="ri-chat-3-line"></i>
                      </BButton>
                    </td>
                  </tr>
                  <tr v-if="expandido === l.contato_id">
                    <td colspan="6" class="bg-body-tertiary">
                      <div v-if="carregandoMensagens" class="text-center py-2"><BSpinner small /></div>
                      <div v-else class="vstack gap-2 py-1">
                        <div v-for="m in mensagens" :key="m.id" class="d-flex gap-2 align-items-start">
                          <span class="badge bg-secondary-subtle text-secondary">{{ m.ordem }}</span>
                          <div>
                            <div class="small">{{ textoResposta(m) }}</div>
                            <small class="text-muted">
                              {{ dataHora(m.recebida_em) }} · {{ duracao(m.minutos_ate_resposta) }} após o envio
                            </small>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="!linhas.length">
                  <td colspan="6" class="text-center text-muted py-4">
                    Ninguém respondeu a esta campanha ainda.
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Abas: sem resposta / falhas -->
            <table v-else class="table align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th>Contato</th>
                  <th>Telefone</th>
                  <th>Situação</th>
                  <th>Enviado em</th>
                  <th v-if="aba === 'sem_resposta'">Lido em</th>
                  <th v-else>Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="l in linhas" :key="l.id">
                  <td>
                    <div class="fw-medium">{{ l.nome || '—' }}</div>
                    <small class="text-muted">{{ local(l) }}</small>
                  </td>
                  <td class="text-nowrap"><small>{{ telefone(l.telefone) }}</small></td>
                  <td><span :class="statusBadge(l.status)">{{ statusLabel(l.status) }}</span></td>
                  <td class="text-nowrap"><small>{{ dataHora(l.enviado_em) }}</small></td>
                  <td v-if="aba === 'sem_resposta'" class="text-nowrap"><small>{{ dataHora(l.lido_em) }}</small></td>
                  <td v-else style="max-width: 320px;">
                    <small class="text-muted">{{ l.erro_mensagem || l.erro_codigo || '—' }}</small>
                  </td>
                </tr>
                <tr v-if="!linhas.length">
                  <td colspan="5" class="text-center text-muted py-4">
                    {{ aba === 'sem_resposta'
                      ? 'Todo mundo que recebeu respondeu.'
                      : 'Nenhuma falha registrada.' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="total > 0" class="d-flex justify-content-between align-items-center mt-3">
            <small class="text-muted">
              {{ (pagina - 1) * 25 + 1 }}–{{ Math.min(pagina * 25, total) }} de {{ total }}
            </small>
            <div class="hstack gap-1">
              <BButton variant="light" size="sm" :disabled="pagina <= 1" @click="irPara(pagina - 1)">
                <i class="ri-arrow-left-s-line"></i>
              </BButton>
              <span class="small text-muted px-2">{{ pagina }} / {{ totalPaginas }}</span>
              <BButton variant="light" size="sm" :disabled="pagina >= totalPaginas" @click="irPara(pagina + 1)">
                <i class="ri-arrow-right-s-line"></i>
              </BButton>
            </div>
          </div>
        </div>
      </BCard>

      <CampanhaRelatorioModal
        :visivel="modalRelatorio"
        :campanha="campanha"
        :resumo="resumo"
        @fechar="modalRelatorio = false"
      />
    </template>

    <BCard v-else no-body>
      <div class="card-body text-center text-muted py-5">
        Campanha não encontrada.
        <div class="mt-2">
          <BButton variant="light" size="sm" @click="voltar">Voltar para campanhas</BButton>
        </div>
      </div>
    </BCard>
  </Layout>
</template>

<style scoped>
.resposta-texto {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  font-size: 13px;
}
.search-box input {
  min-width: 220px;
}
</style>
